import React, { FC, useEffect, useRef, useState, memo, useMemo } from "react";
import { Helmet } from "react-helmet";
import { PromoProps } from "component-promo";
import { Theme, useMediaQuery } from "@mui/material";
import { queryGetWistias } from "graphql-client/queries/get-wistia";
import { useQuery } from "@apollo/client";
import analyticsLoader from "analytics/loader";
import { memoryStorage } from "utils/analytics/storage";
import { processEnvServer } from "hooks/useSsrHooks";
declare global {
  interface Window {
    _wq: Array<any>;
    _wq_disabled_pipedream: boolean;
    _rh_wait_for_wistia_hero: boolean;
    _rh_on_wistia_hero_play: any;
  }
}

export type WistiaProps = PromoProps & {
  /**
   * If set to true, the video will play as soon as it's ready.
   *
   * @see https://wistia.com/support/developers/embed-options#autoplay
   */
  autoPlay?: boolean;
  /**
   * This option allows videos to autoplay in a muted state in contexts where normal autoplay is blocked or not supported (e.g. iOS, Safari 11+, Chrome 66+).
   *
   * Possible values are:
   * - true: The video will default to autoplaying silently.
   * - allow: The video will default to normal autoplay, with silent autoplay as a fallback if needed.
   * - false: The video will not autoplay silently.
   *
   * @see https://wistia.com/support/developers/embed-options#silentautoplay
   */
  silentAutoPlay?: boolean | "allow";
  isResponsive?: boolean;
  onEnd?: () => void;
  wistiaIdSmDown?: string;
  wistiaIdIpad?: string;
  isVisible?: string;
  getCurrentTime?: (currentTime: number) => void;
  backgroundColor?: string;
  isHero?: boolean;

  /** Authorable: seconds into the video when links should appear */
  showLinksAtSec?: number;
  linkAuthorName?: string;
  playOnlyOnce?: boolean;
};

const getAspectRatioFromMeta = (metaVideos, wistiaId) => {
  if (!metaVideos || !wistiaId) return "auto";
  const selected = metaVideos.find(
    metaVideo => metaVideo.wistiaId === wistiaId
  );

  const selectedAspectRatio = selected?.aspectRatio?.replace?.(/[^0-9.]/g, "");
  return selectedAspectRatio;
};

const setCookie = (name: string, value: string, days: number = 30) => {
  if (typeof document === "undefined") return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const getVideoPlayedCookieName = (wistiaId: string) =>
  `wistia_played_${wistiaId}`;

const hasVideoBeenPlayed = (wistiaId: string): boolean => {
  return getCookie(getVideoPlayedCookieName(wistiaId)) === "true";
};

const markVideoAsPlayed = (wistiaId: string) => {
  setCookie(getVideoPlayedCookieName(wistiaId), "true");
};

export const Wistia: FC<WistiaProps> = ({
  autoPlay,
  data,
  isResponsive = true,
  silentAutoPlay,
  wistiaIdSmDown,
  wistiaIdIpad,
  isQuadImpl = false,
  onEnd,
  getCurrentTime,
  customAspectWidth,
  customAspectHeight,
  isHero,
  showLinksAtSec,
  linkAuthorName,
  playOnlyOnce = false
}) => {
  let lastScrollPosition = window.scrollY;
  const isIpadLandscape = useMediaQuery<Theme>(
    theme => theme.breakpoints.between(1365, 1600),
    { noSsr: true }
  );
  const upSm = useMediaQuery<Theme>(theme => theme.breakpoints.up("sm"), {
    noSsr: true
  });

  const chooseWistiaId = useMemo(() => {
    /* for SSR they are only 2 options 0-11199 we use smdown and 1200 up is desktop  */
    if (isIpadLandscape) {
      return wistiaIdIpad || data?.dynamicText?.wistia;
    } else if (upSm) {
      return data?.dynamicText?.wistia;
    } else return wistiaIdSmDown || data?.dynamicText?.wistia;
  }, [isIpadLandscape, upSm]);

  const getPreloads = useMemo(() => {
    const preloads: { media: string; wistiaId: string }[] = [];
    if (wistiaIdIpad) {
      preloads.push({
        wistiaId: wistiaIdIpad,
        media: "(min-width: 1366px) and (max-width: 1600px)"
      });
    }
    if (wistiaIdSmDown) {
      preloads.push({ wistiaId: wistiaIdSmDown, media: "(max-width: 767px)" });
    }
    if (data?.dynamicText?.wistia) {
      /* cant have 2 preload with the same source wo we are picking the 768 until 1365 instead of bigger than 1601*/
      /*  preloads.push({
        wistiaId: data?.dynamicText?.wistia,
        media: "(min-width: 1601px)"
      }); */
      preloads.push({
        wistiaId: data?.dynamicText?.wistia,
        media: "(min-width: 768px) and (max-width: 1365px)"
      });
    }
    return preloads;
  }, [data, wistiaIdSmDown, wistiaIdIpad]);

  const wistiaIds = [wistiaIdIpad, data?.dynamicText?.wistia, wistiaIdSmDown]
    .filter(Boolean)
    .join(",");

  const wistiaId =
    //remove isQuadImpl when we test wistia with new requirement to have mobile under md instead of sm
    isQuadImpl ? data.id : chooseWistiaId;
  const { data: wistiaResponse = {} as Query } = useQuery<Query>(
    queryGetWistias,
    {
      variables: {
        WistiasInput: {
          wistiaId: wistiaIds
        }
      }
    }
  );
  const { getWistias } = (wistiaResponse as any) || {};

  const aspectRatio = getAspectRatioFromMeta(getWistias, wistiaId); // getWistias?.aspectRatio || "16 / 9";
  const [videoPlayer, setVideoPlayer] = useState(null) as any;
  const [bgColor, setBgColor] = useState("black");

  const videoWrapperRef = useRef(null);

  const toggleVideosLinksDisplay = (type = "none") => {
    if (!linkAuthorName) return;
    try {
      const links = document.querySelectorAll(
        `[authoringname="${linkAuthorName}"]`
      );
      if (links && links.length > 0) {
        links.forEach(link => {
          if (link) (link as HTMLElement).style.display = type;
        });
      }
    } catch (e) {
      console.error("Catch error in vimeo link toggleVideosLinksDisplay:", e);
    }
  };

  const setVideoPosition = (player: any, alreadyPlayed: boolean) => {
    if (alreadyPlayed) {
      if (showLinksAtSec) {
        player.time(showLinksAtSec);
      } else {
        const duration = player.duration();
        if (duration) {
          player.time(Math.max(0, duration - 5));
        }
      }
    } else {
      player.time(0);
    }
  };

  useEffect(() => {
    if (!window._wq_disabled_pipedream) {
      window._wq_disabled_pipedream = true;
      window._wq = window._wq || [];
      window._wq.push(function (W) {
        W.consent(false);
      });
    }
    if (!processEnvServer) {
      document?.addEventListener("fullscreenchange", fullScreenEventListener);
      //cleanup function to remove event listener when component unmounts
      return () => {
        document?.removeEventListener(
          "fullscreenchange",
          fullScreenEventListener
        );
      };
    }
  }, []);

  const fullScreenEventListener = () => {
    const scrollYPosition = window.scrollY;
    if (!document.fullscreenElement && scrollYPosition !== lastScrollPosition) {
      window.scrollTo(0, lastScrollPosition);
    }
  };

  useEffect(() => {
    let shouldUpdate = true;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          //in the viewport, play it
          if (videoPlayer) {
            const videoAlreadyPlayed =
              playOnlyOnce &&
              typeof wistiaId === "string" &&
              hasVideoBeenPlayed(wistiaId);
            const shouldAutoPlay = String(autoPlay) && !videoAlreadyPlayed;

            //if video was already played, don't autoplay but keep it at the right position
            if (videoAlreadyPlayed) {
              setVideoPosition(videoPlayer, true);
            } else if (
              videoPlayer.state() === "paused" ||
              (shouldAutoPlay && videoPlayer.state() === "beforeplay")
            ) {
              videoPlayer.play();
            }
          }
        } else {
          //out of the viewport, pause it and restart
          if (videoPlayer) {
            const videoAlreadyPlayed =
              playOnlyOnce &&
              typeof wistiaId === "string" &&
              hasVideoBeenPlayed(wistiaId);

            if (videoPlayer.state() === "playing") {
              videoPlayer.pause();
            }

            // If video was already played, keep it at the right position, otherwise restart
            setVideoPosition(videoPlayer, videoAlreadyPlayed);
          }
        }
      });
    });
    if (!!wistiaId) {
      const win = window as any;
      win._wq = win._wq || [];
      const hasWistiaId = win._wq.some(obj => {
        return obj.id === wistiaId;
      });
      const loadWistia = () => {
        if (!hasWistiaId) {
          //check if video was already played and tracking is enabled
          const videoAlreadyPlayed =
            playOnlyOnce &&
            typeof wistiaId === "string" &&
            hasVideoBeenPlayed(wistiaId);
          const shouldAutoPlay = autoPlay && !videoAlreadyPlayed;

          win._wq.push({
            id: wistiaId,
            options: {
              //autoPlay: shouldAutoPlay ?? true,
              silentAutoPlay: silentAutoPlay ?? "allow",
              // stillUrl: thumbnailUrl,
              preload: "all"
            },
            onReady: (video: any) => {
              if (!video) {
                return;
              }
              if (shouldUpdate && video !== videoPlayer) {
                setVideoPlayer(video);
              }
              if (videoWrapperRef.current) {
                observer.observe(videoWrapperRef.current);
              }

              //if video was already played, set it to the time when links should show
              if (videoAlreadyPlayed) {
                video.ready(() => {
                  if (showLinksAtSec) {
                    video.time(showLinksAtSec); // Set to the time when links appear
                    toggleVideosLinksDisplay("block"); // Show links immediately
                  } else {
                    //if no showLinksAtSec, position near the end but not at the very end
                    const duration = video.duration();
                    if (duration) {
                      video.time(Math.max(0, duration - 5)); // 5 seconds before end
                      toggleVideosLinksDisplay("block");
                    }
                  }
                });
              } else {
                toggleVideosLinksDisplay("none");
              }

              const isFilmVideo =
                !processEnvServer &&
                memoryStorage.getItem("isModalOpen") &&
                document?.querySelector("[data-analytics-id='rh-modal']");

              video.bind("play", () => {
                lastScrollPosition = window.scrollY;
                setBgColor("transparent");

                // mark video as played if tracking is enabled
                if (
                  playOnlyOnce &&
                  typeof wistiaId === "string" &&
                  !hasVideoBeenPlayed(wistiaId)
                ) {
                  markVideoAsPlayed(wistiaId);
                }

                if (
                  isHero &&
                  !processEnvServer &&
                  window._rh_wait_for_wistia_hero
                ) {
                  window._rh_on_wistia_hero_play?.forEach(fn => fn());
                  window._rh_on_wistia_hero_play = [];
                  window._rh_wait_for_wistia_hero = false;
                }

                if (!processEnvServer && isFilmVideo && video.inViewport()) {
                  analyticsLoader(a =>
                    a.emitAnalyticsEvent(
                      document.querySelector("#spa-root > *")! as HTMLElement,
                      a.EVENTS.VIDEO_START.INT_TYPE,
                      {
                        title: video?.name()
                      }
                    )
                  );
                }
              });

              let milestonesReached = new Set<number>();
              let videoCompleted = false;
              let linksShown = false;
              video.bind("timechange", (time: number) => {
                if (!processEnvServer && isFilmVideo && video.inViewport()) {
                  const duration = video?.duration();
                  const percentage = Math.floor((time / duration) * 100);
                  const milestones = [10, 25, 50, 75, 100];

                  const milestoneToTrack = milestones.find(
                    milestone =>
                      percentage >= milestone &&
                      !milestonesReached.has(milestone)
                  );

                  if (
                    milestoneToTrack !== undefined &&
                    isFilmVideo &&
                    !videoCompleted
                  ) {
                    if (milestoneToTrack === 100) {
                      videoCompleted = true;
                      analyticsLoader(a =>
                        a.emitAnalyticsEvent(
                          document.querySelector(
                            "#spa-root > *"
                          )! as HTMLElement,
                          a.EVENTS.VIDEO_COMPLETE.INT_TYPE,
                          {
                            title: video?.name()
                          }
                        )
                      );
                    } else {
                      analyticsLoader(a =>
                        a.emitAnalyticsEvent(
                          document.querySelector(
                            "#spa-root > *"
                          )! as HTMLElement,
                          a.EVENTS.VIDEO_IN_PROGRESS.INT_TYPE,
                          {
                            percentage: milestoneToTrack,
                            title: video?.name()
                          }
                        )
                      );
                      milestonesReached.add(milestoneToTrack);
                    }
                  }
                }
                try {
                  if (showLinksAtSec && !linksShown && time >= showLinksAtSec) {
                    linksShown = true;
                    toggleVideosLinksDisplay("block");
                  }
                } catch (e) {
                  console.error("Catch error in vimeo link toggle:", e);
                }
              });

              if (onEnd) {
                video.bind("end", onEnd);
                if (
                  !processEnvServer &&
                  isFilmVideo &&
                  video.inViewport() &&
                  !videoCompleted
                ) {
                  videoCompleted = true;
                  analyticsLoader(a =>
                    a.emitAnalyticsEvent(
                      document.querySelector("#spa-root > *")! as HTMLElement,
                      a.EVENTS.VIDEO_COMPLETE.INT_TYPE,
                      {
                        title: video?.name()
                      }
                    )
                  );
                }
              }
              //Wistia fires event every second of video. we call get current time with this event.
              if (getCurrentTime) {
                video.bind("secondchange", () => {
                  const currentTime = video.time();
                  getCurrentTime(currentTime);
                });
              }
            }
          });
        }
      };
      loadWistia();
    }
    return () => {
      shouldUpdate = false;
      if (videoWrapperRef.current) {
        observer.unobserve(videoWrapperRef.current);
      }
    };
  }, [wistiaId, videoWrapperRef, setBgColor, showLinksAtSec, playOnlyOnce]);

  return (
    <>
      {/* {wistiaJson && ( */}
      <>
        {/* <Helmet>
            <script>{wistiaJson}</script>
          </Helmet> */}
        {/* { rederClient && <Helmet> <script id="wistia_script" src="https://fast.wistia.com/assets/external/E-v1.js" async ></script> </Helmet> } */}
        <Helmet>
          {isHero && (
            <script data-wistia-clean-up-helmet="true" id="wistia-main-data">{`
            window._rh_wait_for_wistia_hero = true;
          `}</script>
          )}
          <script
            id="wistia_script"
            src="https://fast.wistia.com/assets/external/E-v1.js"
            async
            {...({ fetchpriority: "high" } as any)}
          ></script>
        </Helmet>

        {getPreloads.map(preload => {
          return (
            <Helmet key={preload.media}>
              <link
                rel="preload"
                href={`https://fast.wistia.com/embed/medias/${preload.wistiaId}.json`}
                as="fetch"
                media={preload.media}
                crossOrigin="anonymous"
              />
            </Helmet>
          );
        })}
      </>

      <>
        <div
          ref={videoWrapperRef}
          className="wistia_responsive_padding"
          style={{
            backgroundColor: bgColor,
            aspectRatio: aspectRatio,
            position: "relative",
            marginBottom: "-3px"
          }}
        >
          <div
            className="wistia_responsive_wrapper"
            style={{
              height: "100%",
              left: 0,
              position: "absolute",
              top: 0,
              width: "100%",

              backgroundSize: "contain"
            }}
          >
            <div
              key={wistiaId}
              className={`wistia_embed wistia_async_${wistiaId} seo=false videoFoam=true`}
              style={{ height: "100%", width: "100%" }}
            ></div>
          </div>
        </div>
      </>
    </>
  );
};

Wistia.defaultProps = {
  autoPlay: true,
  data: {
    dynamicText: {
      wistia: ""
    }
  },
  silentAutoPlay: "allow"
} as Partial<WistiaProps>;

export default memo(Wistia);
